/* eslint-disable consistent-return */
import { useCallback } from 'react';
import notify from '../../../utils/notify';

import useStore from '../store';

import { AgentDetails, TeamDetails, type ChatMessage } from '../../../types/os';
import { getAgentsAPI, getStatusAPI, getTeamsAPI } from '../api/os';

const useChatActions = () => {
    const { chatInputRef } = useStore();
    const selectedEndpoint = useStore((state) => state.selectedEndpoint);
    const setSessionId = useStore((state) => state.setSessionId);
    const setMessages = useStore((state) => state.setMessages);
    const setIsEndpointActive = useStore((state) => state.setIsEndpointActive);
    const setIsEndpointLoading = useStore((state) => state.setIsEndpointLoading);
    const setAgents = useStore((state) => state.setAgents);
    const setTeams = useStore((state) => state.setTeams);
    const setSelectedModel = useStore((state) => state.setSelectedModel);
    const setMode = useStore((state) => state.setMode);

    const getStatus = useCallback(async () => {
        try {
            const status = await getStatusAPI(selectedEndpoint);
            return status;
        } catch {
            return 503;
        }
    }, [selectedEndpoint]);

    const getAgents = useCallback(async () => {
        try {
            const agents = await getAgentsAPI(selectedEndpoint);
            return agents;
        } catch {
            notify({
                type: 'failure',
                messageList: ['Error fetching agents'],
            });
            return [];
        }
    }, [selectedEndpoint]);

    const getTeams = useCallback(async () => {
        try {
            const teams = await getTeamsAPI(selectedEndpoint);
            return teams;
        } catch {
            notify({
                type: 'failure',
                messageList: ['Error fetching teams'],
            });
            return [];
        }
    }, [selectedEndpoint]);

    const clearChat = useCallback(() => {
        setMessages([]);
        setSessionId(null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const focusChatInput = useCallback(() => {
        setTimeout(() => {
            requestAnimationFrame(() => chatInputRef?.current?.focus());
        }, 0);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const addMessage = useCallback(
        (message: ChatMessage) => {
            setMessages((prevMessages) => [...prevMessages, message]);
        },
        [setMessages]
    );

    const initialize = useCallback(async () => {
        setIsEndpointLoading(true);
        try {
            const status = await getStatus();
            let agents: AgentDetails[] = [];
            let teams: TeamDetails[] = [];
            if (status === 200) {
                setIsEndpointActive(true);
                agents = await getAgents();
                teams = await getTeams();
                console.log(' is active', teams, agents);
                // Always operate in agent mode with single agent 'cs_brain_skills'
                setMode('agent');
                // Optionally set a default model for UI display
                if (agents.length > 0) {
                    setSelectedModel(agents[0]?.model?.model || '');
                } else if (teams.length > 0) {
                    setSelectedModel(teams[0]?.model?.provider || '');
                } else {
                    setSelectedModel('');
                }
                setAgents(agents);
                setTeams(teams);
            } else {
                setIsEndpointActive(false);
                setMode('agent');
                setSelectedModel('');
            }
            return { agents, teams };
        } catch (error) {
            console.error('Error initializing :', error);
            setIsEndpointActive(false);
            setMode('team');
            setSelectedModel('');
            setAgents([]);
            setTeams([]);
        } finally {
            setIsEndpointLoading(false);
        }
    }, [
        getStatus,
        getAgents,
        getTeams,
        setIsEndpointActive,
        setIsEndpointLoading,
        setAgents,
        setTeams,
        setSelectedModel,
        setMode,

    ]);

    return {
        clearChat,
        addMessage,
        getAgents,
        focusChatInput,
        getTeams,
        initialize
    };
};

export default useChatActions;
